import { observer } from "mobx-react-lite";
import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../types/candidate";
import { useEffect, useRef, useState } from "react";
import { isTruthy } from "remeda";
import { unwrapValueConfig } from "@smartsuite/automation-engine-api";
import type {
  OutputData,
  GetActionTestResponse,
  ValueConfig,
} from "@smartsuite/automation-engine-api";
import { useRootStore } from "../../../../../stores/hooks";
import uniqBy from "lodash/uniqBy";
import { useConverters } from "../../../../../utils/conditions-coverter.hook";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { useEngineClient } from "../../../../../services/engineClient";
import { OutputDetails } from "./components/output-details";
import { ResponseDetails } from "./components/response-details";

const NUM_OF_RETRIES = 10;
const TIME_TO_RELOAD_ACTION_TEST_ITEM = 5000;
const TIME_TO_HIDE_ERROR = 5000;

export const ActionTestSection = observer<{
  action: ActionCandidate;
  onClickTest: (path?: string) => void;
  path?: string;
}>(({ action, onClickTest, path }) => {
  const engineClient = useEngineClient();
  const [outputs, setOutputs] = useState<OutputData[]>([]);
  const { automations } = useRootStore();
  const { candidate } = automations;
  const { trigger } = candidate.self;
  const timezone = candidate.getTimezone();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [actionTestResponse, setActionTestResponse] =
    useState<GetActionTestResponse>();
  const [timeoutId, setTimeoutId] = useState<
    NodeJS.Timeout | string | number
  >();
  const isMounted = useRef(false);

  const { valueConfigToTargetPills } = useConverters();

  const getAttr = (
    value: ValueConfig,
    trigger: TriggerCandidate,
    actions: ActionCandidate[],
    timezone: string,
  ): PillNodeAttrs[] => {
    const attrs = valueConfigToTargetPills(
      value,
      trigger,
      actions,
      timezone,
      (label) => label,
    );
    return attrs.concat(
      value.values
        .flatMap((v) => {
          if (v.value.oneofKind === "keyValue" && v.value.keyValue.value) {
            return getAttr(v.value.keyValue.value, trigger, actions, timezone);
          }
          if (v.value.oneofKind === "list") {
            return v.value.list.values.flatMap((vl) =>
              getAttr(vl, trigger, actions, timezone),
            );
          }
          return undefined;
        })
        .filter(isTruthy),
    );
  };

  useEffect(() => {
    const values: OutputData[] = action.inputs
      .flatMap((input) => {
        return (
          input.value
            ? getAttr(
                unwrapValueConfig(input.value),
                trigger,
                candidate.allActions,
                timezone,
              )
            : []
        ).map((attr) => {
          return {
            outputLabel: attr.title,
            outputId: JSON.parse(attr.value)?.value,
            icon: attr.icon,
            value: {
              metadata: {},
              values: [
                {
                  label: "",
                  icon: "",
                  value: {
                    oneofKind: "string",
                    string: "",
                  },
                },
              ],
            },
          } satisfies OutputData;
        });
      })
      .filter(isTruthy);
    setOutputs(uniqBy(values, "outputId"));
  }, [action]);

  const startLiveReload = (actionTestId: string, numOfRetries = 0): void => {
    setTimeoutId(
      setTimeout(() => {
        void engineClient
          .getTestAction({ actionTestId })
          .then((response) => {
            if (
              response.actionTest?.status.oneofKind === "complete" ||
              response.actionTest?.status.oneofKind === "error"
            ) {
              setActionTestResponse(response);
              onClickTest("test-result");
            } else if (numOfRetries >= NUM_OF_RETRIES) {
              setError("Timeout error");
              setActionTestResponse(undefined);
            } else {
              startLiveReload(actionTestId, numOfRetries + 1);
            }
          })
          .catch((e) => {
            setActionTestResponse(undefined);
            setError(e.message);
          });
      }, TIME_TO_RELOAD_ACTION_TEST_ITEM),
    );
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  useEffect(() => {
    if (actionTestResponse) {
      setLoading(false);
    }
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }, [actionTestResponse]);

  useEffect(() => {
    if (path === "test") {
      setActionTestResponse(undefined);
    }
  }, [path]);

  useEffect(() => {
    setLoading(false);
    const hideErrorId = setTimeout(() => {
      setError(undefined);
    }, TIME_TO_HIDE_ERROR);
    return () => clearTimeout(hideErrorId);
  }, [error]);

  const onChange = (output: OutputData): void => {
    setOutputs((prevOutputs) => {
      return prevOutputs.map((o) =>
        o.outputId === output.outputId ? output : o,
      );
    });
  };

  const onSend = (): void => {
    setLoading(true);
    void engineClient
      .createTestAction({
        actionTest: {
          actionTestId: "",
          solutionId: candidate.self.solutionId,
          action,
          testInputs: outputs,
          status: {
            oneofKind: "requested",
            requested: 0,
          },
        },
      })
      .then(({ actionTestId }) => {
        setError(undefined);
        startLiveReload(actionTestId);
      })
      .catch((e) => {
        setLoading(false);
        setError(e.message);
      });
  };

  return (
    <>
      {path === "test" && (
        <OutputDetails
          outputs={outputs}
          trigger={trigger}
          onSend={onSend}
          onChange={onChange}
          error={error}
          loading={loading}
        />
      )}
      {path === "test-result" && actionTestResponse && (
        <ResponseDetails
          actionTestResponse={actionTestResponse}
          onClickTest={onClickTest}
        />
      )}
    </>
  );
});
